import { useEffect, useRef } from "react";

/** The virus-scan state the API reports on a file (`files.scan_status`). */
export type ScanStatus = "pending" | "clean" | "infected" | "failed";

export const SCAN_REJECTED_MESSAGE = "That file was rejected because it contains malware.";
export const SCAN_FAILED_MESSAGE = "That file could not be scanned and was not accepted.";

/** A file that is not yet cleared to open. Unknown/absent status means clean. */
export function isPendingScan(file: { scanStatus?: string }): boolean {
  return file.scanStatus === "pending";
}

/** A file the scanner refused, which can never be opened. */
export function isRejectedScan(file: { scanStatus?: string }): boolean {
  return file.scanStatus === "infected" || file.scanStatus === "failed";
}

/** Whether download/preview should be offered at all. */
export function isOpenable(file: { scanStatus?: string }): boolean {
  return !isPendingScan(file) && !isRejectedScan(file);
}

/**
 * Re-run `refresh` every few seconds while something is pending.
 *
 * Scanning is currently completed inside the upload request, so a pending row
 * is the exception (a stalled or legacy scan). There is no realtime event for
 * scan completion, so the view polls instead and the "Scanning…" state clears
 * itself once the file turns clean.
 */
export function useScanPolling(active: boolean, refresh: () => void, intervalMs = 4000): void {
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => latest.current(), intervalMs);
    return () => clearInterval(timer);
  }, [active, intervalMs]);
}
